/**
 * Cámaras de HikCentral — inventario, plan de grabación y estado con su causa.
 *
 * Esta pantalla existe para NO repetir el error de los 37 rojos falsos del
 * dashboard de Holanda. El 7-sep-2026 la plataforma reportaba 167 cámaras
 * caídas: 157 colgaban de 86 grabadores caídos y sólo 10 eran fallas propias
 * de la cámara. Por eso el primer número que se ve no es "caídas" sino
 * "caídas por sí mismas", y cada fila dice de qué grabador cuelga.
 *
 * El otro hallazgo que la pantalla pone al frente: 209 cámaras (38 %) no
 * tienen plan de grabación en la plataforma, 144 de ellas vivas.
 */
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CamarasConfigPanel } from "@/components/CamarasConfigPanel";
import { useCamaras } from "@/hooks/useConfig";
import type { CamaraFila } from "@/api/types";
import { cn } from "@/lib/utils";
import { Fragment } from "react";
import { useState } from "react";

const CAUSA: Record<string, { texto: string; variant: "destructive" | "secondary" | "outline" }> = {
  // Su grabador está caído: la cámara no es el problema y arreglarla no
  // arreglaría nada. Se pinta distinto a propósito.
  grabador_caido: { texto: "Su grabador", variant: "secondary" },
  camara_caida: { texto: "Cámara caída", variant: "destructive" },
  ok: { texto: "En línea", variant: "outline" },
};

// Las térmicas y las de grabación no comparten estándar: se miran aparte
// porque su golden será otro (fase 2) y su gente es otra.
const ROL: Record<string, string> = {
  termica: "Térmica",
  fija: "Grabación",
  ptz: "PTZ",
  canal_nvr: "Canal de NVR",
  desconocido: "Sin identificar",
};

function Tarjeta({
  titulo,
  valor,
  nota,
  alerta,
}: {
  titulo: string;
  valor: number | string;
  nota?: string;
  alerta?: boolean;
}) {
  return (
    <div className="rounded-md border bg-card p-3">
      <div className="text-xs text-muted-foreground">{titulo}</div>
      <div
        className={
          alerta ? "text-2xl font-semibold text-destructive" : "text-2xl font-semibold"
        }
      >
        {valor}
      </div>
      {nota && <div className="text-xs text-muted-foreground">{nota}</div>}
    </div>
  );
}

export function CamarasPanel() {
  // Dos vistas de lo mismo: qué cámaras hay y cómo están (inventario), y si su
  // configuración cuadra con la plantilla que Axelia necesita para ver las cámaras (Compat-Axelia). Se separan porque la
  // segunda sólo alcanza a las que tienen IP propia.
  const [vista, setVista] = useState<"inventario" | "configuracion">("inventario");
  const { data, isLoading, isError } = useCamaras();
  // Holanda es la flota que se opera a diario, pero la plataforma tiene más
  // clientes (Bunker, el contact center, unidades móviles). Se filtra, no se
  // esconde.
  const [soloHolanda, setSoloHolanda] = useState(true);
  const [soloProblemas, setSoloProblemas] = useState(true);
  const [rol, setRol] = useState<string>("");
  // Las cámaras cuelgan de su agencia, no van en una lista aparte: para saber
  // qué pasa en una agencia se abre esa fila y ahí está todo.
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set());
  const alternar = (area: string) =>
    setAbiertas((previas) => {
      const copia = new Set(previas);
      if (copia.has(area)) copia.delete(area);
      else copia.add(area);
      return copia;
    });

  const selector = (
    <div className="flex w-fit gap-1 rounded-md border bg-card p-1 text-sm">
      {(
        [
          ["inventario", "Inventario y estado"],
          ["configuracion", "Configuración (Compat-Axelia)"],
        ] as const
      ).map(([v, etiqueta]) => (
        <button
          key={v}
          type="button"
          onClick={() => setVista(v)}
          className={cn(
            "rounded px-3 py-1",
            vista === v ? "bg-muted font-medium" : "text-muted-foreground",
          )}
        >
          {etiqueta}
        </button>
      ))}
    </div>
  );

  if (vista === "configuracion") {
    return (
      <div className="space-y-4">
        {selector}
        <CamarasConfigPanel />
      </div>
    );
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Cargando cámaras…</p>;
  }
  if (isError || !data) {
    return <p className="text-sm text-destructive">No se pudo consultar las cámaras.</p>;
  }

  if (data._status === "sin_configurar") {
    return (
      <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
        {data.mensaje}
      </div>
    );
  }

  // Que AROC no pueda leer la plataforma NO es que se cayeran las 549 cámaras.
  if (data._status === "sin_datos") {
    return (
      <div className="rounded-md border border-amber-500/50 bg-amber-500/5 p-4 text-sm">
        <p className="font-medium">Sin datos de cámaras</p>
        <p className="text-muted-foreground">{data.mensaje}</p>
        {data.motivo && (
          <p className="mt-1 text-xs text-muted-foreground">Motivo: {data.motivo}</p>
        )}
        {data.ultima_lectura && (
          <p className="text-xs text-muted-foreground">
            Última lectura: {new Date(data.ultima_lectura).toLocaleString()}
          </p>
        )}
      </div>
    );
  }

  const t = data.totales;
  const g = data.grabadores;
  const novedades = data.novedades;
  const areas = (data.areas ?? []).filter((a) => !soloHolanda || a.agencia);
  const camaras = (data.camaras ?? []).filter((c) => {
    if (soloHolanda && c.fuera_de_alcance) return false;
    if (rol && c.rol !== rol) return false;
    if (soloProblemas && c.causa !== "camara_caida" && (c.graba || c.estado !== 1))
      return false;
    return true;
  });
  const nNovedades =
    (novedades?.altas.length ?? 0) +
    (novedades?.bajas.length ?? 0) +
    (novedades?.cambios.length ?? 0);

  return (
    <div className="space-y-4">
      {selector}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Tarjeta titulo="Cámaras" valor={t?.camaras ?? 0} nota={`${t?.en_linea ?? 0} en línea`} />
        <Tarjeta
          titulo="Caídas por sí mismas"
          valor={t?.caidas_propias ?? 0}
          nota={`de ${t?.caidas ?? 0} caídas`}
          alerta={(t?.caidas_propias ?? 0) > 0}
        />
        <Tarjeta
          titulo="Caídas por su grabador"
          valor={t?.caidas_por_grabador ?? 0}
          nota="no son fallas de la cámara"
        />
        <Tarjeta
          titulo="Vivas sin grabar"
          valor={t?.sin_grabacion_en_linea ?? 0}
          nota={`${t?.sin_grabacion ?? 0} sin plan en total`}
          alerta={(t?.sin_grabacion_en_linea ?? 0) > 0}
        />
        <Tarjeta titulo="Novedades" valor={nNovedades} nota="vs la foto anterior" />
      </div>

      <p className="text-xs text-muted-foreground">
        {data.plataforma?.produceName} {data.plataforma?.softVersion} ·{" "}
        {t?.fuera_de_alcance ?? 0} cámaras fuera de alcance (otros clientes, no son
        flota Holanda) · {g?.sin_plan_completo ?? 0} de {g?.con_camaras ?? 0} grabadores
        tienen TODAS sus cámaras sin plan de grabación · sin plan significa que desde la
        plataforma no se puede sacar ese video, no que el grabador no grabe por su cuenta ·{" "}
        {data.capturado_en && new Date(data.capturado_en).toLocaleString()}
      </p>

      <div className="flex flex-wrap gap-2 text-sm">
        <button
          type="button"
          onClick={() => setSoloHolanda((v) => !v)}
          className="rounded-md border px-3 py-1 transition-colors hover:bg-muted"
        >
          {soloHolanda ? "Sólo Holanda" : "Todos los clientes"}
        </button>
        <button
          type="button"
          onClick={() => setSoloProblemas((v) => !v)}
          className="rounded-md border px-3 py-1 transition-colors hover:bg-muted"
        >
          {soloProblemas ? "Sólo lo accionable" : "Todas las cámaras"}
        </button>
        <select
          value={rol}
          onChange={(e) => setRol(e.target.value)}
          aria-label="Filtrar por tipo de cámara"
          className="rounded-md border bg-background px-3 py-1 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">Todos los tipos</option>
          {Object.entries(data.por_rol ?? {})
            .sort((a, b) => b[1].total - a[1].total)
            .map(([k, v]) => (
              <option key={k} value={k}>
                {ROL[k] ?? k} ({v.total})
              </option>
            ))}
        </select>
      </div>

      {/* Qué flota es cada cosa, y a cuántas se les puede preguntar su
          configuración: sólo las que tienen IP propia admiten ISAPI. */}
      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
        {Object.entries(data.por_rol ?? {})
          .sort((a, b) => b[1].total - a[1].total)
          .map(([k, v]) => (
            <span key={k} className="rounded-md border px-2 py-1">
              <span className="font-medium text-foreground">{ROL[k] ?? k}</span> {v.total} ·{" "}
              {v.en_linea} en línea · {v.con_ip_propia} con IP propia
            </span>
          ))}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-medium">
          Por agencia — toca una para ver sus cámaras
        </h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Área</TableHead>
              <TableHead>Agencia</TableHead>
              <TableHead className="text-right">Cámaras</TableHead>
              <TableHead className="text-right">Caídas propias</TableHead>
              <TableHead className="text-right">Por su grabador</TableHead>
              <TableHead className="text-right">Sin plan (vivas)</TableHead>
              <TableHead className="text-right">Térmicas</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {areas.map((a) => {
              const abierta = abiertas.has(a.area);
              const suyas = camaras.filter((c) => c.area === a.area);
              return (
                <Fragment key={a.area}>
                  <TableRow
                    onClick={() => alternar(a.area)}
                    className="cursor-pointer"
                    aria-expanded={abierta}
                  >
                    <TableCell className="font-medium">
                      <span className="mr-1 inline-block w-3 text-muted-foreground">
                        {abierta ? "▾" : "▸"}
                      </span>
                      {a.area}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{a.agencia ?? "—"}</TableCell>
                    <TableCell className="text-right">{a.total}</TableCell>
                    <TableCell
                      className={
                        a.caidas_propias > 0
                          ? "text-right font-medium text-destructive"
                          : "text-right"
                      }
                    >
                      {a.caidas_propias}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {a.caidas_por_grabador}
                    </TableCell>
                    <TableCell
                      className={
                        a.sin_grabacion_en_linea > 0
                          ? "text-right font-medium text-amber-600 dark:text-amber-500"
                          : "text-right"
                      }
                    >
                      {a.sin_grabacion_en_linea}
                    </TableCell>
                    <TableCell
                      className={
                        a.termicas > 0 && a.termicas_en_linea < a.termicas
                          ? "text-right font-medium text-destructive"
                          : "text-right text-muted-foreground"
                      }
                    >
                      {a.termicas > 0 ? `${a.termicas_en_linea}/${a.termicas}` : "—"}
                    </TableCell>
                  </TableRow>

                  {abierta && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-muted/40 p-0">
                        {/* Tabla propia con encabezados: una agencia puede
                            tener 21 camaras y en renglones sueltos no hay
                            forma de recorrerlas con la vista. */}
                        <table className="w-full text-xs">
                          <thead className="text-muted-foreground">
                            <tr className="border-b">
                              <th className="px-3 py-1.5 text-left font-medium">Ubicación</th>
                              <th className="px-3 py-1.5 text-left font-medium">Tipo</th>
                              <th className="px-3 py-1.5 text-left font-medium">Estado</th>
                              <th className="px-3 py-1.5 text-left font-medium">Grabación</th>
                              <th className="px-3 py-1.5 text-left font-medium">Grabador</th>
                              <th className="px-3 py-1.5 text-left font-medium">IP</th>
                            </tr>
                          </thead>
                          <tbody>
                            {suyas.map((c: CamaraFila) => {
                              const causa = CAUSA[c.causa ?? "ok"] ?? CAUSA.ok;
                              return (
                                <tr key={c.index_code} className="border-b last:border-0">
                                  <td className="px-3 py-1.5 font-medium">{c.nombre}</td>
                                  <td className="px-3 py-1.5">{ROL[c.rol] ?? c.rol}</td>
                                  <td className="px-3 py-1.5">
                                    <Badge variant={causa.variant}>{causa.texto}</Badge>
                                  </td>
                                  <td className="px-3 py-1.5">
                                    {c.graba ? (
                                      <span className="text-muted-foreground">con plan</span>
                                    ) : (
                                      <span className="font-medium text-amber-600 dark:text-amber-500">
                                        sin plan
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-3 py-1.5 text-muted-foreground">
                                    {c.grabador}
                                  </td>
                                  <td className="px-3 py-1.5 text-muted-foreground">
                                    {c.grabador_ip}
                                  </td>
                                </tr>
                              );
                            })}
                            {suyas.length === 0 && (
                              <tr>
                                <td colSpan={6} className="px-3 py-2 text-muted-foreground">
                                  Ninguna cámara de esta agencia pasa los filtros de arriba.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {nNovedades > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-medium">
            Novedades desde{" "}
            {data.comparado_con && new Date(data.comparado_con).toLocaleString()}
          </h3>
          <ul className="space-y-1 text-sm">
            {novedades?.altas.map((c) => (
              <li key={`alta-${c.index_code}`}>
                <Badge variant="outline">Alta</Badge> {c.nombre} · {c.area}
              </li>
            ))}
            {novedades?.bajas.map((c) => (
              <li key={`baja-${c.index_code}`}>
                <Badge variant="destructive">Baja</Badge> {c.nombre} · {c.area}
              </li>
            ))}
            {novedades?.cambios.map((c) => (
              <li key={`cambio-${c.index_code}-${c.campo}`}>
                <Badge variant="secondary">{c.campo}</Badge> {c.nombre} ·{" "}
                <span className="text-muted-foreground">
                  {String(c.antes)} → {String(c.ahora)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
